import {AppState} from '../states/app.state';
import {createSelector} from '@ngrx/store';
import {identity} from 'rxjs';
import {BelegbucheinAusgangDTO, PartnerAuswahlDTO, UpdateBelegRequestDTO} from '../../openapi/beleg-openapi';
import {BelegEntitiesSelectors} from './beleg-entities.selectors';
import * as moment from 'moment/moment';

export class EditSelectors {
  /**
   * Die `select`-Eigenschaft ist ein Objekt, das Funktionen definiert, jede Funktion selektiert eine
   * bestimmte Eigenschaft aus dem State.
   * Das sind grundlegende Selektoren, die im ganzen Projekt als Bausteine für komplexere Selektoren wiederverwendet
   * werden können.
   */
  private static select = {
    belegId: (state: AppState) => state.edit?.belegId,
    belegIds: (state: AppState) => state.edit?.belegIds,
    formFieldBelegeingang: (state: AppState) => state.edit?.formFieldBelegeingang,
    formFieldBelegdatum: (state: AppState) => state.edit?.formFieldBelegdatum,
    formFieldVorgangsartId: (state: AppState) => state.edit?.formFieldVorgangsartId,
    formFieldPartnerbezeichnung: (state: AppState) => state.edit?.formFieldPartnerbezeichnung,
    formFieldBetrag: (state: AppState) => state.edit?.formFieldBetrag,
    formFieldDauerbeleg: (state: AppState) => state.edit?.formFieldDauerbeleg,
    formFieldZahlungsart: (state: AppState) => state.edit?.formFieldZahlungsart,
    formFieldKommentar: (state: AppState) => state.edit?.formFieldKommentar,
    formBlockValidBeleg: (state: AppState) => state.edit?.formBlockValidBeleg,
    openOverviewPage: (state: AppState) => state.edit?.openOverviewPage,
  };

  public static belegId = createSelector(this.select.belegId, identity);

  public static belegIds = createSelector(this.select.belegIds, identity);

  public static belegIdsCount = createSelector(this.select.belegIds, belegIds => {
    if (!belegIds || belegIds.length === 0) return 1;
    return belegIds.length;
  });

  public static currentBelegIndex = createSelector(this.select.belegId, this.select.belegIds, (belegId, belegIds) => {
    if (!belegId || !belegIds || belegIds.length === 0) return 1;
    return belegIds.indexOf(belegId) + 1;
  });

  public static formFieldBelegeingang = createSelector(this.select.formFieldBelegeingang, identity);

  public static formFieldBelegdatum = createSelector(this.select.formFieldBelegdatum, identity);

  public static formFieldVorgangsartId = createSelector(this.select.formFieldVorgangsartId, identity);

  public static formFieldPartnerbezeichnung = createSelector(this.select.formFieldPartnerbezeichnung, identity);

  public static formFieldBetrag = createSelector(this.select.formFieldBetrag, identity);

  public static formFieldDauerbeleg = createSelector(this.select.formFieldDauerbeleg, identity);

  public static formFieldZahlungsart = createSelector(this.select.formFieldZahlungsart, identity);

  public static formFieldKommentar = createSelector(this.select.formFieldKommentar, identity);

  public static isFormValid = createSelector(this.select.formBlockValidBeleg, formBlockValidBeleg => {
    return formBlockValidBeleg;
  });

  public static updateBelegRequestDto = createSelector(
    BelegEntitiesSelectors.all,
    this.select.belegId,
    this.select.formFieldBelegeingang,
    this.select.formFieldBelegdatum,
    this.select.formFieldVorgangsartId,
    this.select.formFieldPartnerbezeichnung,
    this.select.formFieldBetrag,
    this.select.formFieldDauerbeleg,
    this.select.formFieldZahlungsart,
    this.select.formFieldKommentar,
    (
      belege,
      belegId,
      belegeingang,
      belegdatum,
      vorgangsartId,
      partnerbezeichnung,
      betrag,
      dauerbeleg,
      zahlungsart,
      kommentar,
    ) => {
      // INFO: Ermitteln des BelegDtos.
      const belegDto = belege.find(beleg => {
        return beleg.id === belegId;
      });

      if (!belegDto) {
        return;
      }

      // INFO: Erstellen des PartnerAuswahlDTOs.
      let updatedPartnerAuswahlDto: PartnerAuswahlDTO;
      const currentPartnerAuswahlDto = belegDto.partner;

      // INFO: Prüfen, ob ein ADNOVA+ Partner eingetragen ist.
      if (currentPartnerAuswahlDto && currentPartnerAuswahlDto.id) {
        // INFO: Prüfen, ob die Bezeichnung unverändert ist.
        const currentBezeichnung = currentPartnerAuswahlDto.bezeichnung;
        if (currentBezeichnung === partnerbezeichnung) {
          // INFO: Wurde die Bezeichnung nicht geändert, so bleibt der ADNOVA+ Partner unverändert
          updatedPartnerAuswahlDto = {
            ...currentPartnerAuswahlDto,
          };
        } else {
          // INFO: Wurde die Bezeichnung geändert, so wird diese übernommen und der ADNOVA+ Partner entfernt.
          updatedPartnerAuswahlDto = {
            bezeichnung: partnerbezeichnung!,
          };
        }
      } else {
        // INFO: Ist kein ADNOVA+ Partner eingetragen, wird nur die Bezeichnung übernommen.
        updatedPartnerAuswahlDto = {
          bezeichnung: partnerbezeichnung!,
        };
      }

      // INFO: Ermitteln der Datums-Werte
      const datum = moment(belegdatum, 'DD.MM.YYYY').format('YYYY-MM-DD');
      const jahr = moment(belegdatum!, 'DD.MM.YYYY').year();

      // INFO: Erstellen des UpdateBelegRequestDTOs.
      const updateDto: UpdateBelegRequestDTO = {
        belegeinAusgang: belegeingang ? BelegbucheinAusgangDTO.Belegeingang : BelegbucheinAusgangDTO.Belegausgang,
        vorgangsartId: vorgangsartId!,
        partner: updatedPartnerAuswahlDto,
        datum,
        jahr,
        kommentar: kommentar && kommentar.length > 0 ? kommentar : undefined,
        betrag: betrag !== undefined && betrag !== null ? betrag : undefined,
        dauerbeleg,
        zahlungsart,

        //INFO: Weitere Felder, die wir in unserer UI nicht bedienen.
        erlaeuterung: belegDto.erlaeuterung ?? undefined,
        stichwortIds: belegDto.stichworte?.map(stichwort => stichwort.id as string),
        labelIds: belegDto.label?.map(label => label.id),
        ordnerId: belegDto.ordner?.id,
      };

      return updateDto;
    },
  );

  public static openOverviewPage = createSelector(this.select.openOverviewPage, identity);
}
